import {ICoordinateAdjustmentService} from "../coordinate-adjustment";
import {L} from "../../lt-ng/landcare-toolkit";
import calculateAdjustedCenter = L.map.calculateAdjustedCenter;
import {Pixel} from "ol/pixel";
import {Map as OlMap} from 'ol';
import {Extent} from "ol/extent";
import {Coordinate} from "ol/coordinate";

type AppMode = 'xs-landscape' | 'xs' | 'sm-portrait' | 'sm-landscape' | 'lg';


export class TransparentPaneCoordinateAdjustmentService implements ICoordinateAdjustmentService {
	public static $inject = [];

	constructor() {

	}

	protected _left: HTMLElement;
	get left(): HTMLElement {
		this._left  = this._left ||  document.querySelector('.map-pane.map-pane-left');
		return this._left;
	}


	protected _right: HTMLElement;

	get right(): HTMLElement {
		this._right  = this._right ||  document.querySelector('.map-pane.map-pane-right');
		return this._right;
	}

	protected _bottom: HTMLElement;

	get bottom(): HTMLElement {
		this._bottom  = this._bottom ||  document.querySelector('.map-pane.map-pane-bottom');
		return this._bottom;
	}

	protected _inset: HTMLElement;

	get inset(): HTMLElement {
		this._inset  = this._inset ||  document.querySelector('.inset-pane');
		return this._inset;
	}

	private _mapPaneSm: HTMLElement;

	get mapPaneSm(): HTMLElement {
		this._mapPaneSm  = this._mapPaneSm ||  document.querySelector('.map-pane-sm');
		return this._mapPaneSm;
	}

	private _toolbar: HTMLElement;
	get toolbar() : HTMLElement {
		this._toolbar = this._toolbar || document.getElementById('MapControls');
		return this._toolbar;
	}

	protected get appMode(): AppMode {
		if (window.innerWidth < 767 && window.innerWidth < window.innerHeight) {
			return 'xs';
		}

		if( window.innerWidth < 767 && window.innerHeight < window.innerWidth) {
			return 'xs-landscape';
		}

		if (this.inset.clientHeight || this.mapPaneSm.clientWidth) {
            const isPortrait = window.innerHeight > window.innerWidth;
			return isPortrait ? 'sm-portrait' : 'sm-landscape'
		}

		return 'lg';
	}

	protected getMapTopLeftPixel() : Pixel {
		let mode = this.appMode;

		const toolbarOverlap = this.toolbar ? this.toolbar.getBoundingClientRect().height : 0;

		let left = 0;
		if (mode == 'sm-landscape') {
			left = this.inset.getBoundingClientRect().right;
		}
		if (mode == 'lg') {
			left = this.left.getBoundingClientRect().right;
		}

		let isXs = mode == 'xs' || mode == 'xs-landscape';

		return [
			left,
			isXs ? 0 : toolbarOverlap
		]
	}

	protected getBottomRight(map: OlMap) : Pixel {
		let mode = this.appMode;

		let size = map.getSize();

		if(mode == 'xs') {
			return [
				size[0],
				size[1] - this.inset.getBoundingClientRect().height
			];
		}

		if (mode == 'xs-landscape') {
			return [
				size[0] - this.inset.getBoundingClientRect().width,
				size[1]
			];
		}

		if (mode == 'sm-landscape') {
			return size;
		}

		if (mode == 'sm-portrait'){
			return [
				size[0],
				size[1] - this.inset.getBoundingClientRect().height
			];
		}

		return [
			size[0] - this.right.getBoundingClientRect().width,
			size[1] - this.bottom.getBoundingClientRect().height
		]
	}

	getVisibleMapExtent(map: OlMap): Extent {
		let topLeftPixel: Pixel = this.getMapTopLeftPixel();
		let bottomRightPixel: Pixel = this.getBottomRight(map);

		let topLeftCoordinate = map.getCoordinateFromPixel(topLeftPixel);
		let bottomRightCoordinate = map.getCoordinateFromPixel(bottomRightPixel);

		return <[number, number, number, number]> [
			Math.min(topLeftCoordinate[0], bottomRightCoordinate[0]),
			Math.min(topLeftCoordinate[1], bottomRightCoordinate[1]),
			Math.max(topLeftCoordinate[0], bottomRightCoordinate[0]),
			Math.max(topLeftCoordinate[1], bottomRightCoordinate[1])
		];
	}


	/**
	 * Given a coordinate A, this method returns a coordinate B, which, when the map
	 * is centred on B, the coordinate A will be at the centre area of the map which isn't covered by panes.
	 * @param {Coordinate} coordinate
	 * @param {OlMap} map
	 * @param {number} zoomLevel
	 * @returns {Coordinate}
	 */
	adjustCentreForScreenSpace(coordinate: Coordinate, map: OlMap, zoomLevel: number): Coordinate {
		let mapSize = map.getSize();

		let pixel: Pixel;
		let mode = this.appMode;

		if (mode === 'sm-portrait') {
			// There is one pane at the bottom of the screen, but it isn't transparent
			// the centre does not need to be adjusted
			pixel = [
				mapSize[0] / 2,
				mapSize[1] / 2
			];
		} else if (mode === 'sm-landscape') {
			// There is one pane to the left of the screen,
			// adjust the centre of the map rightwards
			let width = mapSize[0] - this.mapPaneSm.clientWidth;
			pixel = [
				width / 2 + this.mapPaneSm.clientWidth,
				mapSize[1] / 2
			]
		} else if (mode === 'xs') {
			// xs mode and the inset pane is open
			// adjust the centre upwards
			let height = mapSize[1] - this.inset.clientHeight;
			pixel = [
				mapSize[0] / 2,
				height / 2
			]
		} else if (mode == 'xs-landscape') {
			pixel = [
				(mapSize[0] - this.inset.clientWidth) / 2,
				mapSize[1] / 2
			]
		} else { // mode === 'lg'; // implicitly
			// md, larger, or xs with the inset pane close
			// take into account each panel
			let width = mapSize[0] - this.left.clientWidth - this.right.clientWidth;

			let x = this.left.clientWidth + width / 2;

			let height = mapSize[1] - this.bottom.clientHeight;

			let y = height / 2;

			pixel = [x, y];
		}

		return calculateAdjustedCenter(coordinate, map.getView(), pixel, map.getSize(), zoomLevel);
	}


	padding(extraPadding: number[] = []): number[] {
		let l = 0;
		let t = 0;
		let r = 0;
		let b = 0;
		let mode = this.appMode;
		if (mode === 'sm-landscape') {
			// small mode
			// There is one pane to the left of the screen,
			l = this.mapPaneSm.clientWidth;

		} else if (mode === 'xs') {
			// xs mode and the inset pane is open
			b = this.inset.clientHeight;
		} else if(mode === 'lg') {
			// md, larger, or xs with the inset pane close
			// take into account each panel
			l = this.left.clientWidth;
			r = this.right.clientWidth;
			b = this.bottom.clientHeight;
		} // implicitly ignore the sm-portrait case, as it has no effect on padding

		let padding =  [t, r, b, l];

		if (extraPadding.length >= 4) {
			extraPadding.forEach((e, i) => padding[i] += e);
		}

		return padding;
	}


	getCentreAdjustedForScreenSpace(map: OlMap): Coordinate {
		let mode = this.appMode;
		let pixel = <Pixel> map.getSize().map((n) => n / 2);
		if (mode === 'xs') {
			pixel[1] -= this.inset.clientHeight / 2;
		}
		if (mode === 'sm-landscape') {
			pixel[0] += this.mapPaneSm.clientWidth / 2;
		}

		// implicitly ignore 'sm-portrait' case, don't alter the centre

		if (mode === 'lg') {
			pixel[0] += (this.left.clientWidth - this.right.clientWidth) / 2;
			pixel[1] -= this.bottom.clientHeight / 2;
		}

		return map.getCoordinateFromPixel(pixel);
	}
}